import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { updateCallFeedback } from '@/features/calls/api'
import type { CallDetail } from '@/features/calls/types'
import { callDetailKeys } from '@/lib/queryKeys'

export function useUpdateCallFeedbackMutation(callId: string) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const callDetailKey = callDetailKeys.detail(callId, 'supabase')

  return useMutation({
    mutationFn: async (callFeedback: string | null) => {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      return updateCallFeedback(token, callId, callFeedback)
    },
    scope: { id: `call-feedback-${callId}` },
    onMutate: async (callFeedback) => {
      await queryClient.cancelQueries({ queryKey: callDetailKey })
      const previousCallDetail = queryClient.getQueryData<CallDetail | null>(
        callDetailKey
      )

      queryClient.setQueryData<CallDetail | null>(callDetailKey, (current) =>
        current ? { ...current, call_feedback: callFeedback } : current
      )

      return { previousCallDetail }
    },
    onError: (_error, _callFeedback, context) => {
      if (typeof context?.previousCallDetail !== 'undefined') {
        queryClient.setQueryData(callDetailKey, context.previousCallDetail)
      }
    },
    onSuccess: (response) => {
      queryClient.setQueryData<CallDetail | null>(callDetailKey, (current) =>
        current
          ? { ...current, call_feedback: response.data.call_feedback }
          : current
      )
    },
  })
}
